// Core
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Custom layout
import MainLayout from "../../../Layouts/Main";

//styles
import "./styles.scss";

//assets
import assets from "../../../Assets";

//components
import InputBox from "Components/InputBox";
import NormalTable from "Components/Common/Table";
import Pagination from "Components/Pagination";

//utils
import { notifySuccess } from "Services/Utils/toastNotification";

//redux
import { useDispatch } from "react-redux";
import {
  closeUserChatRequest,
  getContactDetailsRequest,
  getContactRequestList,
  postReplyToUserRequest,
} from "../../../Redux/Slices/contactManagementSlice";
import Modal from "Components/Common/Modal";
import Button from "Components/Button";
import TextArea from "Components/TextArea";

const ContactRequestManagement = () => {
  const dispatch = useDispatch();

  const [contactList, setContactList] = useState([]);
  const [contactDetails, setContactDetails] = useState({});
  const [viewModal, setViewModal] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [search, setSearch] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortOptions, setSortOptions] = useState({
    sort_by: false,
    order_by: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [modalStatus, setModalStatus] = useState(true); // create for the modal status checking remove it when api intergration
  const [showReply, setShowReply] = useState(false);
  const [reply, setReply] = useState("");
  const [comments, setComments] = useState("");
  const [errorReplyMessage, setErrorReplyMessage] = useState("");
  const [errorCommentMessage, setErrorCommentMessage] = useState("");

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    // const formattedTime = date.toLocaleTimeString("en-GB", {
    //   hour: "2-digit",
    //   minute: "2-digit",
    //   hour12: true,
    // });
    return `${formattedDate} `;
  };
  

  const headerDetails = [
    {
      label: "Issue ID",
      sortKey: false,
    },
    {
      label: "Customer Name",
      sortKey: true,
      singleClickFunc: () => {
        setSortOptions({
          sort_by: !sortOptions.sort_by,
          order_by: "firstname",
        });
      },
    },
    {
      label: "Preference Type",
      sortKey: true,
      singleClickFunc: () => {
        setSortOptions({
          sort_by: !sortOptions.sort_by,
          order_by: "contact_type",
        });
      },    },
    {
      label: "Concern",
      sortKey: false,
    },
    {
      label: "Date Submitted On",
      sortKey: true,
      singleClickFunc: () => {
        setSortOptions({
          sort_by: !sortOptions.sort_by,
          order_by: "createdAt",
        });
      },    },
    {
      label: "Status",
      sortKey: true,
      singleClickFunc: () => {
        setSortOptions({
          sort_by: !sortOptions.sort_by,
          order_by: "acknowledgment_status",
        });
      },    },
    {
      label: "Action",
      sortKey: false,
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    setCurrentPage(1)
  }, [search])

  useEffect(() => {
    setIsLoading(true);
    getContactListApiCall();
  }, [sortOptions, currentPage, search]);

  const getContactListApiCall = () => {
    dispatch(
      getContactRequestList({
        data: {
          search: search,
          page: currentPage,
          limit: "10",
          sort_by: sortOptions.sort_by ? "ASC" : "DESC",
          order_by: sortOptions.order_by,
        },
        callBack: getContactRequestListCB,
      })
    );
  };

  const getContactRequestListCB = (value) => {
    setContactList(value?.data?.data_list || []);
    setTotalPage(value.data.pageMeta.totalPages);
    setTotalCount(value?.data?.pageMeta?.total || 0);
    setIsLoading(false);
  };

  const handleOutsideClick = (e) => {
    // Check if clicked outside and if the menu is open
    if (openMenuId !== null && !e.target.closest(".optionsMenu")) {
      setOpenMenuId(null); // Close the menu if clicked outside
    }
  };

  const toggleMenu = (id) => {
    setOpenMenuId(openMenuId === id ? null : id);
    // console.log(openMenuId)
  };

  useEffect(() => {
    // Add the event listener with a slight delay to allow the toggleMenu logic to complete
    const timer = setTimeout(() => {
      document.addEventListener("click", handleOutsideClick);
    }, 0);

    // Clean up event listener on unmount or when the component re-renders
    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [openMenuId]);

  const handleInputChange = (event) => {
    setReply(event.target.value);
    if (errorReplyMessage) {
      // Clear error message when the user starts typing
      setErrorReplyMessage("");
    }
  };

  const handleInputCommentChange = (event) => {
    setComments(event.target.value);
    if (errorCommentMessage) {
      // Clear error message when the user starts typing
      setErrorCommentMessage("");
    }
  };

  const handleSubmit = (id) => {
    if (comments.trim() === "") {
      setErrorCommentMessage("Please enter a comment before submitting.");
      return;
    } else {
      dispatch(
        closeUserChatRequest({
          contactId: id,
          data: {
            acknowledgment_status: 3,
            admin_comment: comments,
          },
          callBack: closeUserChatRequestCB,
        })
      );
    }
  };

  const closeUserChatRequestCB = () => {
    notifySuccess("Internal Comment Sent!");
    setCloseModal(false);
    setErrorCommentMessage("");
    setContactDetails({});
    setShowReply(false);
    getContactListApiCall();
  };

  const handeleSend = (id) => {
    if (reply.trim() === "") {
      setErrorReplyMessage("Please enter a reply before submitting.");
      return;
    } else {
      dispatch(
        postReplyToUserRequest({
          contactId: id,
          data: {
            reply_message: reply,
          },
          callBack: postReplyToUserRequestCB,
        })
      );
    }
  };

  const postReplyToUserRequestCB = () => {
    notifySuccess("Reply sent!");
    setViewModal(false);
    setErrorReplyMessage("");
    setShowReply(false);
    setContactDetails({});
    getContactListApiCall();
  };

  const getContactDetailsRequestCB = (data) => {
    setContactDetails(data?.data);
  };

  return (
    <MainLayout pageName={"Contact Enquiries"}>
      <div className="ultra-user-management-container">
        <div className="ultra-user-management-header">
          <InputBox
            value={search}
            icon={assets.Icons.searchIcon}
            onChange={(e) => setSearch(e.target.value)}
            inputStyle={{
              padding: "1px 2vh 1px 7vh",
              border: "none",
              height: "43px",
              width: "25vw",
              borderRadius: "0px",
              borderBottom: "1px solid #C1C7CD",
              boxShadow: "none",
              color: 'black'
            }}
          />
          <p className="totalCount">
            Total count : {totalCount}
          </p>
        </div>
        <div className="ultra-user-management-table">
          <NormalTable headerDetails={headerDetails}>
            {contactList.length > 0
              ? contactList.map((contact, index) => (
                  <tr
                    key={contact?.contact_us_id}
                    style={{ position: "relative" }}
                  >
                    <td style={{ paddingLeft:"18px" }}>
                      {contact?.contact_us_id}
                    </td>
                    <td style={{ display: "flex", alignItems: "center" }}>
                      {contact?.user_details?.firstname}
                    </td>
                    <td>{contact?.contact_type}</td>
                    <td>{contact?.concern}</td>
                    <td>
                    {formatDateTime(contact?.createdAt) || "-"}
                    </td>
                    <td
                      style={{
                        color:
                          contact?.acknowledgment_status === 1
                            ? "#FF482F"
                            : contact?.acknowledgment_status === 2
                            ? "#ffcc00"
                            : "#10CA23",
                      }}
                    >
                      {contact?.acknowledgment_status === 1
                        ? "Open"
                        : contact?.acknowledgment_status === 2
                        ? "Inprogress"
                        : "Closed"}
                    </td>
                    <td>
                      <img
                        style={{ height: "15px", cursor: "pointer" }}
                        src={assets.Icons.optionsIcon}
                        alt="Options"
                        onClick={() => toggleMenu(contact?.contact_us_id)}
                      />
                      {openMenuId === contact?.contact_us_id && (
                        <div className="optionsMenuContainer">
                          <ul>
                            <li
                              onClick={() => {
                                setViewModal(true);
                                dispatch(
                                  getContactDetailsRequest({
                                    data: {
                                      contactId: contact?.contact_us_id,
                                    },
                                    callBack: getContactDetailsRequestCB,
                                  })
                                );
                              }}
                            >
                              <img src={assets.Icons.viewIcon} alt="" />
                              View
                            </li>
                            {contact?.acknowledgment_status !== 3 && (
                              <li
                                onClick={() => {
                                  setCloseModal(true);
                                  dispatch(
                                    getContactDetailsRequest({
                                      data: {
                                        contactId: contact?.contact_us_id,
                                      },
                                      callBack: getContactDetailsRequestCB,
                                    })
                                  );
                                }}
                              >
                             <p style={{color:'lightgray', fontWeight:"700", fontSize:'18px', marginRight:'10px', marginLeft:'5px'}}>X</p>                              
                             Close
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              : ""}
          </NormalTable>
        </div>
        {contactList.length > 0 ? (
          ""
        ) : (
          <p style={{ textAlign: "center", fontFamily: "poppins", fontSize: "4vh" }}> {isLoading ? "Loading" : "No Data Found" } </p>
        )}
        <div className="ultra-user-management-table-pagination">
          {contactList.length > 0 ? (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPage}
              onPageChange={handlePageChange}
            />
          ) : (
            ""
          )}
        </div>
      </div>

      <Modal
        open={viewModal}
        closeModal={() => {
          setViewModal(false);
          setErrorReplyMessage("");
          setReply("");
          setShowReply(false);
        }}
        modalStyle={{ maxWidth: "650px" }}
      >
        <div className="viewModalContainer_Contact">
          <div className="viewModalHeading">
            <p>Issue Details</p>
            <div
              className={`viewModaltitle ${
                contactDetails?.acknowledgment_status === 1
                  ? "viewModaltitle-statusOpen"
                  : contactDetails?.acknowledgment_status === 2
                  ? "viewModaltitle-statusInprogress"
                  : "viewModaltitle-statusClosed"
              }`}
            >
              <div>
                {contactDetails?.acknowledgment_status === 1
                  ? "Open"
                  : contactDetails?.acknowledgment_status === 2
                  ? "Inprogress"
                  : "Closed"}
              </div>
            </div>
          </div>

          <div className="customerDetails-modal">
            <div>Customer information</div>
            <table>
              <tr>
                <th>Name</th>
                <th>Preference Type</th>
                <th>Mobile</th>
                <th>Email</th>
              </tr>
              <tr>
                <td>{contactDetails?.user_details?.firstname}</td>
                <td>{contactDetails?.contact_type}</td>
                <td>
                  {/* {contactDetails?.country_code}  */}
                  {contactDetails?.mobile}</td>
                <td>{contactDetails?.email}</td>
              </tr>
            </table>
          </div>

          <div className="customerDetails-modal">
            <div>Issue Details</div>
            <table>
              <tr>
                <th>Concern</th>
                <th>Date Submitted</th>
                <th>Message</th>
              </tr>
              <tr>
                <td>{contactDetails?.concern}</td>
                <td>
                  {" "}
                  {new Date(contactDetails?.updatedAt).toLocaleDateString(
                    "en-GB"
                  ) || "-"}
                </td>
                <td>{contactDetails?.message}</td>
              </tr>
            </table>
          </div>

          <div className="internalComment-modal">
            {contactDetails?.acknowledgment_status === 2 ||
            contactDetails?.acknowledgment_status === 3 ? (
              <>
                <div className="internalcomment-title">Reply</div>
                <div className="internalComment-comment">
                  {contactDetails?.reply_to_user}
                </div>
              </>
            ) : null}
            {contactDetails?.acknowledgment_status === 3 ? (
              <>
                <div className="internalcomment-title">Internal Comment</div>
                <div className="internalComment-comment">
                  {contactDetails?.admin_comment}
                </div>
              </>
            ) : (
              <>
                <div>
                  {contactDetails?.acknowledgment_status === 1 && !showReply ? (
                    <Button
                      onClick={() => setShowReply(true)}
                      buttonStyle={{
                        height: "2vh",
                        width: "200px",
                        float: "right",
                      }}
                      title="Reply to customer"
                    />
                  ) : null}

                  {showReply && (
                    <div className="reply-section-modal">
                      <h3>Reply To Customer</h3>
                      <TextArea
                        placeholder="Write your reply..."
                        value={reply}
                        onChange={handleInputChange}
                      />
                      {errorReplyMessage && (
                        <p
                          style={{
                            color: "red",
                            fontSize: "14px",
                            marginBottom: "10px",
                            fontFamily: "inter",
                          }}
                        >
                          {errorReplyMessage}
                        </p>
                      )}
                      <Button
                        title="Send"
                        onClick={() =>
                          handeleSend(contactDetails?.contact_us_id)
                        }
                        buttonStyle={{
                          height: "2vh",
                          width: "200px",
                          alignSelf: "flex-end",
                        }}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        open={closeModal}
        closeModal={() => {
          setCloseModal(false);
          setComments("");
          setErrorCommentMessage("");
        }}
      >
        <div className="closeModalContainer_Contact">
          <h3>Add credits to</h3>
          <TextArea
            placeholder="Enter Internal Comments here"
            value={comments}
            onChange={handleInputCommentChange}
          />
          {errorCommentMessage && (
            <p
              style={{
                color: "red",
                fontSize: "14px",
                marginBottom: "10px",
                fontFamily: "inter",
              }}
            >
              {errorCommentMessage}
            </p>
          )}
          <div>
            <Button
              onClick={() => handleSubmit(contactDetails?.contact_us_id)}
              buttonStyle={{ height: "2vh", width: "100px", float: "right" }}
              title="Submit"
            />
          </div>
        </div>
      </Modal>
    </MainLayout>
  );
};

export default ContactRequestManagement;
